import { useCallback, useMemo, useEffect } from "react";
import { useAppSelector } from "@store/index";
import { lineChartConfigSelector } from "@store/slices/line-chart-config-slice";
import {
  useGetDatasetQuestionsQuery,
  useGetDatasetsQuery,
  useGetWeightConfigurationsQuery,
} from "@/store/api/dataset-api";

interface PerLineWeight {
  surveyId: string;
  configurationId: string;
  datasetElementQuestionId: string;
  datasetElementVariableId: string;
  weightVariableLabel: string;
}

interface UsePerLineWeightsProps {
  datasetId: string;
  lineLabels: string[]; // Array of line labels (series names) from chart
  lineIds: string[]; // Array of line IDs (series.id) from chart
  onWeightsChange: (weights: Record<string, PerLineWeight>) => void;
}

export const usePerLineWeights = ({
  datasetId,
  lineLabels,
  lineIds,
  onWeightsChange,
}: UsePerLineWeightsProps) => {
  // Read perLineWeights directly from Redux
  const { currentConfig } = useAppSelector(lineChartConfigSelector);
  const perLineWeights = useMemo(
    () => currentConfig.perLineWeights || {},
    [currentConfig.perLineWeights],
  );

  // Fetch all datasets to get the surveyId
  const { data: datasets = [] } = useGetDatasetsQuery({});

  // Find the current dataset and get the first surveyId
  const surveyId = useMemo(() => {
    const dataset = datasets.find((d) => d.id === datasetId);
    return dataset?.surveyIds?.[0] || "";
  }, [datasets, datasetId]);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId,
    { skip: !datasetId },
  );

  const isMultiSurvey = useMemo(() => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const surveyCount = dataset?.surveyIds?.length ?? 0;
    if (surveyCount <= 1) return false;

    const configs = weightConfigData?.weightConfigurations ?? [];
    if (configs.length !== surveyCount) return true;

    const first = configs[0];
    const allSame = configs.every(
      (c) =>
        c.datasetElementQuestionId === first.datasetElementQuestionId &&
        c.variableLabel === first.variableLabel,
    );
    return !allSame;
  }, [datasets, datasetId, weightConfigData]);

  // Fetch weight questions for the dataset using surveyId
  // Filter by FLOAT question type and multidimensional = true
  // IMPORTANT: This query is isolated and should not interfere with line chart question selection
  const {
    data: weightQuestionsData,
    isLoading: isLoadingWeights,
    error: weightConfigError,
  } = useGetDatasetQuestionsQuery(
    {
      id: datasetId,
      survey_id: surveyId,
      question_type: ["FLOAT"],
      multidimensional: true,
      page: 1,
      page_size: 100, // Get all weight questions
    },
    {
      skip: !datasetId || !surveyId,
      // Keep this data in cache longer to avoid refetching
      // This prevents interference with line chart question queries
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  // Transform questions into WeightConfiguration format
  const weightConfigurations = useMemo(() => {
    if (!weightQuestionsData?.questions || !surveyId) {
      return [];
    }

    return weightQuestionsData.questions.map((question) => ({
      configurationId: question.datasetElementId,
      surveyId: surveyId,
      surveyName: "", // Not available from questions endpoint, but not displayed in UI
      datasetElementQuestionId: question.datasetElementId,
      questionLabel: question.label || question.title || question.name,
    }));
  }, [weightQuestionsData, surveyId]);

  // Update line weight - calls the Redux action directly
  const updateLineWeight = useCallback(
    (
      lineId: string,
      surveyId: string,
      configurationId: string,
      datasetElementQuestionId: string,
      datasetElementVariableId: string,
      weightVariableLabel: string,
    ) => {
      const newWeights = {
        ...perLineWeights,
        [lineId]: {
          surveyId,
          configurationId,
          datasetElementQuestionId,
          datasetElementVariableId,
          weightVariableLabel,
        },
      };

      console.log(`💾 updateLineWeight for line "${lineId}":`, {
        lineId,
        weightVariableLabel,
        surveyId,
        newWeights,
        previousWeights: perLineWeights,
      });

      onWeightsChange(newWeights);
    },
    [perLineWeights, onWeightsChange],
  );

  // Clear weight for a specific line (use default weight)
  const clearLineWeight = useCallback(
    (lineId: string) => {
      console.log(`🧹 Clearing weight for line: ${lineId}`);
      const updated = { ...perLineWeights };
      delete updated[lineId];
      onWeightsChange(updated);
    },
    [perLineWeights, onWeightsChange],
  );

  // Clear all weights (set all to default weight)
  const clearAllWeights = useCallback(() => {
    console.log("🧹 Clearing all weights - resetting to default");
    onWeightsChange({});
  }, [onWeightsChange]);

  // Get weight for a specific line
  const getLineWeight = useCallback(
    (lineId: string): PerLineWeight | undefined => {
      return perLineWeights[lineId];
    },
    [perLineWeights],
  );

  // Check if all lines are configured
  const isAllLinesConfigured = useMemo(() => {
    if (lineIds.length === 0) return false;
    return lineIds.every((lineId) => {
      const weight = perLineWeights[lineId];
      return (
        weight && weight.configurationId && weight.datasetElementVariableId
      );
    });
  }, [lineIds, perLineWeights]);

  // Clean up weights for lines that no longer exist
  // IMPORTANT: Only clean up if we have lines loaded (don't clean when lineIds is empty during initial load)
  useEffect(() => {
    // Skip cleanup if no lines are loaded yet (e.g., when editing widget before preview data loads)
    if (lineIds.length === 0) {
      return;
    }

    const currentLines = new Set(lineIds);
    const existingLines = Object.keys(perLineWeights);

    // Check if any lines need to be removed
    const needsCleanup = existingLines.some(
      (lineId) => !currentLines.has(lineId),
    );

    if (needsCleanup) {
      console.log("🧹 Cleaning up weights for removed lines");
      const filtered: Record<string, PerLineWeight> = {};
      for (const [lineId, weight] of Object.entries(perLineWeights)) {
        if (currentLines.has(lineId)) {
          filtered[lineId] = weight;
        }
      }
      onWeightsChange(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineIds]); // Intentionally NOT including perLineWeights or onWeightsChange to avoid infinite loops

  return {
    datasetId,
    surveyId,
    isMultiSurvey,
    perLineWeights,
    weightConfigurations,
    isLoadingWeights,
    weightConfigError,
    updateLineWeight,
    clearLineWeight,
    clearAllWeights,
    getLineWeight,
    isAllLinesConfigured,
  };
};
